import { RefObject } from 'react';
import moment from 'moment';
import FeedbackStatus from './FeedbackStatus';
import FeedbackActions from './FeedbackActions';
import { ITipsFeedbackItem, TFeedbackDetailsSection } from '../../../../interfaces/tips-feedback/ITipsFeedback';

const tableColumns = ['Date', 'Client', 'Property', 'Feedback', 'Status'];

const formatDate = (value?: string) => {
    if (!value || !moment(value).isValid()) return '--';
    return moment.utc(value).format('YYYY-MM-DD');
};

const TipsFeedbackTable = ({
    rows,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    onOpenDetails,
    scrollContainerRef,
}: {
    rows?: ITipsFeedbackItem[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    onOpenDetails?: (row: ITipsFeedbackItem, section: TFeedbackDetailsSection) => void;
    scrollContainerRef?: RefObject<HTMLDivElement>;
}) => {
    const handleScroll = () => {
        if (!scrollContainerRef?.current || !hasMore || loadingMore || loading) return;
        const el = scrollContainerRef.current;
        if (el.scrollHeight <= el.clientHeight || el.scrollTop <= 0) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) onLoadMore?.();
    };

    return (
        <div className='overflow-x-auto border border-gray-200 rounded-2xl dark:border-borderColor-darkMode'>
            <div ref={scrollContainerRef} onScroll={handleScroll} className='overflow-y-auto' style={{ maxHeight: '650px' }}>
                <table className='min-w-full bg-white dark:bg-black dark:text-white min-h-[220px]'>
                    <thead className='sticky top-0 z-10 bg-gray-100 dark:bg-gray-900 text-left text-sm font-medium text-gray-500 dark:text-gray-300'>
                        <tr>
                            {tableColumns.map((column) => (
                                <th key={column} scope='col' className='min-w-[130px] px-4 py-4 font-medium'>{column}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !rows?.length && Array.from({ length: 8 }).map((_, index) => (
                            <tr key={index} className='animate-pulse'>
                                {tableColumns.map((column) => (
                                    <td key={column} className='px-4 py-4'>
                                        <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {!loading && !rows?.length && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No feedback records currently available
                                </td>
                            </tr>
                        )}
                        {rows?.map((row, index) => (
                            <tr key={row._id || index} onContextMenu={(event) => {
                                event.preventDefault();
                                onOpenDetails?.(row, 'details');
                            }} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm'>
                                <td className='px-4 py-4 whitespace-nowrap'>{formatDate(row.date || row.dateOfTip)}</td>
                                <td className='px-4 py-4 min-w-[180px]'>
                                    <p className='max-w-[260px] break-words'>{row.client || row.userCreated?.companyData?.username || '--'}</p>
                                </td>
                                <td className='px-4 py-4 min-w-[200px]'>
                                    <p className='max-w-[300px] break-words'>{row.property || '--'}</p>
                                </td>
                                <td className='px-4 py-4 min-w-[320px]'>
                                    <div className='flex items-start gap-2'>
                                        <button type='button' onClick={() => onOpenDetails?.(row, 'details')}
                                            aria-label={`View feedback details for ${row.property || row.client || 'feedback'}`}
                                            className='min-w-0 flex-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-700'>
                                            <span className='max-w-[520px] break-words' title={row.feedback}
                                                style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {row.feedback || '--'}
                                            </span>
                                        </button>
                                        <FeedbackActions item={row} onSelect={onOpenDetails} />
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <FeedbackStatus status={row.workflowStatus} label={row.status} />
                                </td>
                            </tr>
                        ))}
                        {loadingMore && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-5 text-center text-sm text-gray-500 dark:text-gray-400' role='status'>
                                    Loading more feedback...
                                </td>
                            </tr>
                        )}
                        {!loading && !loadingMore && !!rows?.length && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-4 text-center text-xs text-gray-500 dark:text-gray-400'>
                                    {hasMore === false && 'All feedback records loaded'}
                                    {hasMore && <button type='button' onClick={onLoadMore} className='text-sm underline underline-offset-4'>Load more feedback</button>}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TipsFeedbackTable;
